/**
 * Staff work surfaces: Dashboard (portfolio-led KPIs), Applications decision
 * queue, and KYC review — all on region-scoped platform endpoints.
 */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportButton } from "@/components/staff/ExportButton";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Row = Record<string, any>;

const STATUS_CLASSES: Record<string, string> = {
  active: "bg-success/10 text-success",
  late: "bg-warning/10 text-warning",
  paid_off: "bg-muted text-muted-foreground",
  pending: "bg-primary/10 text-primary",
  approved: "bg-success/10 text-success",
  rejected: "bg-destructive/10 text-destructive",
};

export const statusBadge = (status: string) => (
  <Badge className={STATUS_CLASSES[status] || "bg-muted text-muted-foreground"}>{status}</Badge>
);

// ============ Applications queue ============

export function ApplicationsQueue() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    staffApi.applications("?status=pending&limit=100")
      .then((d) => setRows(d.applications))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast({ title: ok });
      setRejecting(null); setReason("");
      load();
    } catch (error) {
      toast({ title: "Action failed", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Applications</h1>
          <p className="text-sm text-muted-foreground">Pending decisions — approve creates the loan and schedule.</p>
        </div>
        <div className="flex gap-2">
          <ExportButton dataset="applications" />
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>
        </div>
      </header>
      <Card>
        <CardContent className="pt-4">
          {loading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin" /> : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Queue is clear — no pending applications.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Applied</TableHead><TableHead>Amount</TableHead><TableHead>Term</TableHead>
                <TableHead>Purpose</TableHead><TableHead className="text-right">Decision</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm">{a.application_date ? new Date(a.application_date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="font-medium tabular-nums">{formatCurrency(a.amount)}</TableCell>
                    <TableCell>{a.term_months} mo</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.purpose || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" disabled={busy === a.id}
                          onClick={() => { setBusy(a.id); act(() => staffApi.approveApplication(a.id), "Approved — loan and schedule created"); }}>
                          {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" />Approve</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRejecting(a)}>
                          <XCircle className="mr-1 h-4 w-4" />Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejecting} onOpenChange={(open) => !open && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject application</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {rejecting && `${formatCurrency(rejecting.amount)} over ${rejecting.term_months} months. The reason is shown to the customer.`}
          </p>
          <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button variant="destructive" disabled={reason.trim().length < 3}
              onClick={() => rejecting && act(() => staffApi.rejectApplication(rejecting.id, reason.trim()), "Rejected")}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ KYC review ============

export function KycQueue() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    staffApi.kycQueue().then((d) => setRows(d.records)).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const review = async (id: string, action: string, note?: string) => {
    try {
      await staffApi.reviewKyc(id, action, note);
      toast({ title: action === "approved" ? "KYC approved" : "KYC rejected" });
      setRejecting(null); setNotes("");
      load();
    } catch (error) {
      toast({ title: "Review failed", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">KYC Review</h1>
        <p className="text-sm text-muted-foreground">Identity documents awaiting a decision. Every view is audited.</p>
      </header>
      <Card>
        <CardContent className="pt-4">
          {loading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin" /> : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nothing waiting — the review queue is clear.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Phone</TableHead><TableHead>Document</TableHead>
                <TableHead>Files</TableHead><TableHead className="text-right">Decision</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm">{r.customer_phone || "—"}</TableCell>
                    <TableCell className="text-sm">{r.document_type}</TableCell>
                    <TableCell>
                      {Array.from({ length: Number(r.files) || 0 }).map((_, i) => (
                        <Button key={i} size="sm" variant="link" className="h-auto px-1"
                          onClick={() => window.open(staffApi.kycFileUrl(r.id, i), "_blank")}>
                          file {i + 1}
                        </Button>
                      ))}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" onClick={() => review(r.id, "approved")}>
                          <CheckCircle2 className="mr-1 h-4 w-4" />Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRejecting(r)}>
                          <XCircle className="mr-1 h-4 w-4" />Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejecting} onOpenChange={(open) => !open && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject KYC</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Give the customer something actionable ("photo is blurry — retake in daylight").</p>
          <Input placeholder="Note to the customer (required)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button variant="destructive" disabled={notes.trim().length < 3}
              onClick={() => rejecting && review(rejecting.id, "rejected", notes.trim())}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
